import {ResourceModel} from '@/models/content/resource';
import { taxonomySerializer } from './taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { findAnyCompetencyTitle, getResourceFormat, gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class ResourceSerializer {
  private static INSTANCE = new ResourceSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeResource(resource: any): ResourceModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = resource.thumbnail ?
      contentCdnUrl + resource.thumbnail :
      `${window.location.origin}` + DEFAULT_IMAGES_PATH.unit;
    const result: ResourceModel = {
      id: resource.id,
      title: resource.title,
      description: resource.description,
      subformat: resource.contentSubFormat,
      format: resource.format || 'resource',
      efficacy: resource.efficacy,
      relevance: resource.relevance,
      engagement: resource.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(resource.taxonomy),
      creator: taxonomySerializer.normalizeCreator(resource.creator),
      owner: taxonomySerializer.normalizeCreator(resource.creator),
      isVisibleOnProfile: false,
      thumbnailUrl,
    };
    return result;
  }

  public nomalizeSearchResources(payload: any) {
    const resultSet = {
      searchResults: [],
      hitCount: payload.totalHitCount,
    };
    if (Array.isArray(payload.searchResults)) {
      const results = payload.searchResults.map((result: any) => {
        return this.normalizeResource(result);
      });
      resultSet.searchResults = results;
    }
    return resultSet;
  }

  /**
   * Normalized data of resource by id
   * @return {Object}
   */
  public normalizeResourceContent(response: any) {
    const thumbnail = response.thumbnail;
    const cdnUrls = sessionService.getCdnUrl();
    if (!thumbnail) {
      response.thumbnail = DEFAULT_IMAGES_PATH.profile;
    } else {
      response.thumbnail = cdnUrls.user + thumbnail;
    }
    response.metadata = response.metadata || {};
    const taxonomy = taxonomySerializer.normalizeTaxonomyObject(response.taxonomy);
    response.taxonomy = taxonomy[0];
    return response;
  }

  public normalizeResource(resource: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const basePath =  contentCdnUrl;
    const appRootPath = window.location.href; // configuration appRootPath
    const userBasePath = cdnUrls.user_cdn_url;

    const format = getResourceFormat(resource.contentSubFormat);
    const taxonomyInfo =
      (resource.taxonomySet &&
        resource.taxonomySet.curriculum &&
        resource.taxonomySet.curriculum.curriculumInfo) ||
      [];
    const standards =  taxonomySerializer.normalizeTaxonomyArray(taxonomyInfo, false);
    return {
      id: resource.gooruOid,
      title: resource.title,
      description: resource.description || findAnyCompetencyTitle(standards) || resource.title,
      format,
      url: resource.url,
      subformat: resource.contentSubFormat,
      thumbnailUrl: resource.thumbnail
        ? basePath + resource.thumbnail
        : DEFAULT_IMAGES_PATH.unit,
      creator: resource.creator
        ? taxonomySerializer.normalizeCreator(resource.creator)
        : null,
      owner: resource.user ? taxonomySerializer.normalizeOwner(resource.user) : null,
      type: 'resource',
      standards,
      taxonomySet: resource.taxonomySet,
      taxonomySubject: resource.taxonomySet
        ? resource.taxonomySet.subject
        : null,
      taxonomyCourse: resource.taxonomySet ? resource.taxonomySet.course : null,
      taxonomyDomain: resource.taxonomySet ? resource.taxonomySet.domain : null,
      publishStatus: resource.publishStatus,
      publisher: resource.publisher ? resource.publisher[0] : null,
      efficacy: resource.efficacy ? resource.efficacy : null,
      relevance: resource.relevance ? resource.relevance : null,
      engagement: resource.engagement ? resource.engagement : null,
      publisherQualityIndicator: resource.publisherQualityIndicator,
    };
  }

  public normalizePhrasecloud(payload: any) {
    const resources = payload.phrasecloud || [];
    return resources;
  }

  public resourcePulloutSerializer(
    collection: any,
    selectedResource: any,
    metadataLevels: any,
    summaryAndTranscript: any = []) {

    let resultSet = {};
    const defaultVectorValue = 0.5;
    if (collection) {
      const advertisement_level = metadataLevels.advertisement_level;
      const audience = metadataLevels.audience;
      const grade = metadataLevels.grade;
      const License = metadataLevels.license;
      const educational_use = metadataLevels.educational_use;
      const interest = metadataLevels.interest;
      const difficultyLevel = metadataLevels.difficulty_level;
      const engagementLevel = metadataLevels.engagement_level;
      const emotionalTone = metadataLevels.emotional_tone;
      const targetPersonas = metadataLevels.target_persona;
      const instructionalStrategy = metadataLevels.instructional_strategy;
      const readingLevel = metadataLevels.reading_level;

      const gradeLevelText: any = [];
      const info = collection.info || {};
      const collectionGrade = collection.metadata
        ? collection.metadata.grade
        : null;

      if (collectionGrade) {
        const grades = Array.isArray(collectionGrade) ? collectionGrade : [collectionGrade];
        grades.forEach((gradeLevel: any) => {
          gradeLevelText.push(grade[gradeLevel]);
        });
      }

      const moreMetadata = summaryAndTranscript.length ? summaryAndTranscript[0] : {};

      const gooruSubject = info.gooru_subject ? info.gooru_subject.join(', ') : null;
      const gooruCourse = info.gooru_course ? info.gooru_course.join(', ') : null;
      const createdBy = selectedResource.creator.username
        || selectedResource.creator.usernameDisplay
        || selectedResource.creator.firstName || '';
      const createdOn = collection.created_at || collection.publish_date
        ? moment(collection.created_at || collection.publish_date).format('MMMM DD, YYYY')
        : '--';
      resultSet = {
        'descriptive': {
          'title': collection.title || selectedResource.title || '--',
          'description': truncateString(collection.description) || truncateString(selectedResource.description) || '--',
          'Published By': (info && info.publisher) ? info.publisher.toString() : 'Gooru org',
          'Published Status': 'Published',
          'address': collection.url ? collection.url : 'No',
          'License': collection.license
            ? License[collection.license]
            : 'Public Domain',
          'created by': createdBy,
          'created on': createdOn,
          'modified on': collection.modeified_date
            || createdOn
            || '--',
          'modified by': collection.modified_by || createdBy || '--',
          ...( info.start_time && { 'start time': info.start_time || '--' } ),
          ...( info.end_time && { 'end time': info.end_time || '--' } ),
        },

        'instructional': {
          'Learning Objective': collection.metadata.learning_objective || '--',
          'subject': selectedResource.taxonomySet
            ? selectedResource.taxonomySet.subject
            : gooruSubject,
          'course': selectedResource.taxonomySet
            ? gutCourseNames(selectedResource.taxonomySet.course).join(', ')
            : gooruCourse,
          'domain': selectedResource.taxonomySet
            ? selectedResource.taxonomySet.domain
            : null,
          ...(collection.metadata && collection.metadata.grade && { grade: gradeLevelText.join(', ') }),
          'standard': selectedResource.standards ? selectedResource.standards : null,
          ...(collection.metadata && collection.metadata.audience && {
            audience: collection.metadata
              ? collection.metadata.audience
                ? audience[collection.metadata.audience]
                : null
              : null,
          }),
          'Depth of Knowledge':
          collection.depthofknowledge.map((item: any) => item.label).toString()
          || moreMetadata.DepthOfKnowledge,
          'Reading Level': Array.isArray(collection.metadata.reading_level)
            ? collection.metadata.reading_level
              .flatMap((id: number) => readingLevel[String(id)] || []).join(', ')
            : readingLevel[String(collection.metadata.reading_level)] || null,
         '21st Century Skills': collection.skills || moreMetadata.centurySkills || '--',
         'language': 'English',
          'educational use': collection.metadata.educational_use
            ? educational_use[collection.metadata.educational_use]
            : '--',
          'grade': collection.metadata
            ? collection.metadata.grade
              ? gradeLevelText.join(', ')
              : null
            : null,
         'age-range': moreMetadata.ageRange || '--',
         'Editorial Range': selectedResource.publisherQualityIndicator,
          'keywords': collection.info
            ? collection.info.keywords
              ? collection.info.keywords[0]
              : null
            : null,
          ...(
            collection.metadata.instructional_strategy && {
              'Instructional Strategy': Array.isArray(collection.metadata.instructional_strategy)
                ? collection.metadata.instructional_strategy
                  .flatMap((id: number) => instructionalStrategy[String(id)] || []).join(', ')
                : instructionalStrategy[String(collection.metadata.instructional_strategy)] || null,
            }
          ),
          ...(
            collection.metadata.target_personas && {
              'Target Personas': Array.isArray(collection.metadata.target_personas)
                ? collection.metadata.target_personas
                  .flatMap((id: number) => targetPersonas[String(id)] || []).join(', ')
                : targetPersonas[String(collection.metadata.target_personas)] || null,
            }
          ),
          ...(
            collection.metadata.difficulty_level && {
              'Difficulty Level': Array.isArray(collection.metadata.difficulty_level)
                ? collection.metadata.difficulty_level
                  .flatMap((id: number) => difficultyLevel[String(id)] || []).join(', ')
                : difficultyLevel[String(collection.metadata.difficulty_level)] || null,
            }
          ),
        },

        'content analysis': {
          'summary': moreMetadata.summary || null,
          'transcript': moreMetadata.transcript || null,
          'phrasecloud': moreMetadata.phrasecloud || null,
          'accessbility': collection.accessbility || moreMetadata.accessbility || '--',
          'format': collection.content_subformat
            ? collection.content_subformat.replace(/_/g, ' ')
            : null,
          'media Features': collection.media || moreMetadata.mediaFeatures || '--',
          'access hazard': collection.accesshazard
            ? collection.accesshazard
            : 'None',
          'advertisement level': collection.metadata
            ? collection.metadata.advertisement_level
              ? advertisement_level[collection.metadata.advertisement_level]
              : 'Low'
            : 'Low',
          'framebreaker': collection.display_guide
            ? collection.display_guide.is_frame_breaker === 1
              ? 'Yes'
              : 'No'
            : 'No',
          // 'Is Broken': collection.publish_date.is_broken
          //   ? collection.publish_date.is_broken
          //   : 'No',
          ...(info.segment_text && { 'segment text': info.segment_text || '--' }),
          ...(
            collection.metadata.interests_covered && {
              'Interests Covered': Array.isArray(collection.metadata.interests_covered)
                ? collection.metadata.interests_covered
                  .flatMap((id: number) => interest[String(id)] || []).join(', ')
                : interest[String(collection.metadata.interests_covered)] || null,
            }
          ),
          ...(
            collection.metadata.emotional_tone && {
              'Emotional Tone': Array.isArray(collection.metadata.emotional_tone)
                ? collection.metadata.emotional_tone
                  .flatMap((id: number) => emotionalTone[String(id)] || []).join(', ')
                : emotionalTone[String(collection.metadata.emotional_tone)] || null,
            }
          ),
          ...(collection.metadata.engagement_level && {
            'Engagement Level': Array.isArray(collection.metadata.engagement_level)
              ? collection.metadata.engagement_level
                .flatMap((id: number) => engagementLevel[String(id)] || []).join(', ')
              : engagementLevel[String(collection.metadata.engagement_level)] || null,
          }),
          ...(collection.metadata.etl && { ETL: collection.metadata.etl + ' mins' || '--' }),
        },
        'outcomes': {
          relevance: selectedResource.relevance || defaultVectorValue,
          engagement: selectedResource.engagement || defaultVectorValue,
          efficacy: selectedResource.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;

  }

}

export const resourceSerializer = ResourceSerializer.instance;
